'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, Mail, Phone, ChevronDown } from 'lucide-react';

const sections = [
    {
        id: 'information-we-collect',
        icon: Database,
        title: 'Information We Collect',
        content: [
            {
                subtitle: 'Information You Provide',
                body: 'When you contact us, fill out a form, or start a project with Lead Workz, we may collect personal information such as your name, email address, phone number, company name, and any details you share in your message.',
            },
            {
                subtitle: 'Information Collected Automatically',
                body: 'When you visit our website, we automatically collect certain information about your device and usage, including your IP address, browser type, operating system, pages visited, time spent on pages, and the referring URL. This data is collected through cookies and similar tracking technologies.',
            },
            {
                subtitle: 'Cookies & Tracking',
                body: 'We use cookies to improve your experience, analyse website traffic, and understand where our visitors come from. You can control cookie settings through your browser. Disabling cookies may affect certain features of the website.',
            },
        ],
    },
    {
        id: 'how-we-use',
        icon: Eye,
        title: 'How We Use Your Information',
        content: [
            {
                subtitle: 'To Respond to Enquiries',
                body: 'We use your contact information to respond to your questions, project requests, and service enquiries in a timely manner.',
            },
            {
                subtitle: 'To Deliver Our Services',
                body: 'If you become a client of Lead Workz, we use your information to plan, execute, and manage your digital marketing campaigns, website projects, or other services you have engaged us for.',
            },
            {
                subtitle: 'To Improve Our Website',
                body: 'We analyse usage data to understand how visitors interact with our website, identify areas for improvement, and optimise content and performance.',
            },
            {
                subtitle: 'Marketing Communications',
                body: 'With your consent, we may send you relevant updates, insights, or marketing communications. You can unsubscribe from these at any time by clicking the unsubscribe link in any email or contacting us directly.',
            },
        ],
    },
    {
        id: 'data-sharing',
        icon: Shield,
        title: 'How We Share Your Information',
        content: [
            {
                subtitle: 'We Do Not Sell Your Data',
                body: 'Lead Workz does not sell, trade, or rent your personal information to third parties under any circumstances.',
            },
            {
                subtitle: 'Trusted Service Providers',
                body: 'We may share your information with trusted third-party service providers who assist us in operating our website and delivering our services — such as email delivery platforms, analytics tools, and cloud infrastructure providers. These parties are contractually obligated to keep your data confidential and use it only for the purpose of providing services to us.',
            },
            {
                subtitle: 'Legal Requirements',
                body: 'We may disclose your information if required by law, government request, court order, or to protect the rights, property, or safety of Lead Workz, our clients, or others.',
            },
        ],
    },
    {
        id: 'data-security',
        icon: Lock,
        title: 'Data Security',
        content: [
            {
                subtitle: 'How We Protect Your Data',
                body: 'We implement industry-standard security measures including SSL encryption, secure data storage, and access controls to protect your personal information from unauthorised access, disclosure, alteration, or destruction.',
            },
            {
                subtitle: 'Limitations',
                body: 'While we take reasonable precautions, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we continually review and improve our practices to keep your data safe.',
            },
        ],
    },
    {
        id: 'your-rights',
        icon: Shield,
        title: 'Your Rights',
        content: [
            {
                subtitle: 'Access & Correction',
                body: 'You have the right to request access to the personal information we hold about you, and to request corrections if any information is inaccurate or incomplete.',
            },
            {
                subtitle: 'Deletion',
                body: 'You may request that we delete your personal data. We will honour such requests unless we are required by law to retain the information or have a legitimate business reason to keep it.',
            },
            {
                subtitle: 'Opt-Out',
                body: 'You can opt out of marketing communications at any time. You may also disable cookies through your browser settings to limit data collection on our website.',
            },
        ],
    },
    {
        id: 'third-party-links',
        icon: Eye,
        title: 'Third-Party Links',
        content: [
            {
                subtitle: 'External Websites',
                body: 'Our website may contain links to third-party websites such as our social media profiles (Instagram, LinkedIn, Facebook, YouTube). We are not responsible for the privacy practices or content of those external sites. We encourage you to review the privacy policies of any third-party sites you visit.',
            },
        ],
    },
    {
        id: 'changes',
        icon: Database,
        title: 'Changes to This Policy',
        content: [
            {
                subtitle: 'Policy Updates',
                body: 'We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. When we make changes, we will update the effective date at the top of this page. We encourage you to review this page periodically to stay informed about how we protect your information.',
            },
        ],
    },
];

function AccordionItem({ section, index }: { section: typeof sections[0]; index: number }) {
    const [open, setOpen] = useState(false);
    const Icon = section.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.06 }}
            className="border border-gray-200 rounded-2xl overflow-hidden"
        >
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-4 px-8 py-6 text-left hover:bg-gray-50 transition-colors duration-200"
            >
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <span
                        className="text-lg font-semibold text-gray-900"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        {section.title}
                    </span>
                </div>
                <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="px-8 pb-8 space-y-6 border-t border-gray-100">
                    {section.content.map((item, i) => (
                        <div key={i} className="pt-6">
                            <h3
                                className="text-sm font-bold uppercase tracking-widest text-blue-600 mb-2"
                                style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                            >
                                {item.subtitle}
                            </h3>
                            <p
                                className="text-gray-600 leading-relaxed"
                                style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                            >
                                {item.body}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
}

export default function PrivacyPolicyPage() {
    return (
        <main className="min-h-screen bg-white">

            {/* ── Hero ── */}
            <section className="relative pt-40 pb-20 px-6 md:px-24 bg-white overflow-hidden">
                {/* Background grid */}
                <div
                    className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{
                        backgroundImage: 'linear-gradient(#3333AA 1px, transparent 1px), linear-gradient(90deg, #3333AA 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
                <div className="max-w-4xl mx-auto relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold tracking-widest uppercase mb-6">
                            <Shield className="w-3.5 h-3.5" />
                            Legal
                        </span>
                        <h1
                            className="text-5xl md:text-7xl font-black text-gray-900 leading-[0.95] mb-6 tracking-tight"
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            Privacy <span className="text-blue-600">Policy</span>
                        </h1>
                        <p
                            className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed"
                            style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                        >
                            At Lead Workz, your privacy matters to us. This policy explains what information we collect, how we use it, and how we keep it safe.
                        </p>
                        <p className="mt-4 text-sm text-gray-400" style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}>
                            Effective Date: <strong className="text-gray-600">1 January 2025</strong> &nbsp;·&nbsp; Last Updated: <strong className="text-gray-600">1 June 2026</strong>
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── Quick Summary Banner ── */}
            <section className="px-6 md:px-24 pb-12">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-blue-600 rounded-3xl px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-6"
                    >
                        {[
                            { icon: Database, title: 'We collect only what we need', desc: 'Only essential data for communication and service delivery.' },
                            { icon: Lock, title: 'We never sell your data', desc: 'Your information is never sold or traded to third parties.' },
                            { icon: Mail, title: 'You stay in control', desc: 'Request access, corrections, or deletion of your data anytime.' },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="font-bold text-white text-sm leading-snug mb-1" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>{title}</p>
                                    <p className="text-white/70 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}>{desc}</p>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ── Accordion Sections ── */}
            <section className="px-6 md:px-24 pb-24">
                <div className="max-w-4xl mx-auto space-y-4">
                    {sections.map((section, i) => (
                        <AccordionItem key={section.id} section={section} index={i} />
                    ))}
                </div>
            </section>

            {/* ── Contact Section ── */}
            <section className="px-6 md:px-24 pb-32">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="border border-gray-200 rounded-3xl p-10 text-center"
                    >
                        <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
                            <Phone className="w-6 h-6 text-blue-600" />
                        </div>
                        <h2
                            className="text-3xl md:text-4xl font-black text-gray-900 mb-4"
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            Have Questions About Your Privacy?
                        </h2>
                        <p
                            className="text-gray-500 mb-8 max-w-lg mx-auto leading-relaxed"
                            style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                        >
                            If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please don't hesitate to reach out.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                href="/contact"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-blue-600 text-white font-bold text-sm tracking-wide hover:bg-blue-700 transition-colors duration-200"
                                style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                Contact Us
                            </Link>
                            <a
                                href="mailto:info@leadworkz.com"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-gray-200 text-gray-700 font-bold text-sm tracking-wide hover:border-blue-600 hover:text-blue-600 transition-colors duration-200"
                                style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                <Mail className="w-4 h-4" />
                                info@leadworkz.com
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

        </main>
    );
}
